/**
 * The render channel's wire contract, owned by the grid chunk.
 *
 * The kernel resolves display text, alignment, and styles (see
 * `src/kernel/view.ts`); the Grid_Renderer paints them. Both sides need the same
 * shapes and the same flag bits, and where those live matters for the bundle: this
 * module has **no runtime imports at all** — `CellStyle` is a type-only import, so
 * it is erased at build time — which is what keeps a painter that reads
 * `tile.styles[ref].bold` from dragging the Format_Manager, the Data_Store, and the
 * evaluator into the initial chunk (Requirement 14.3).
 *
 * The kernel side imports these declarations rather than restating them, so the two
 * ends of the channel cannot drift.
 */

import type { CellStyle } from '../core/format/style.js';
import type { Addr, CellValue, Range, SheetId } from '../core/types.js';

/**
 * A style as the painter sees it.
 *
 * Identical to the Format_Manager's `CellStyle` on purpose: the kernel interns
 * effective styles and ships them as they are, and an adapter between two
 * identical shapes would be a place for bugs to live rather than a boundary.
 */
export type RenderStyle = CellStyle;

/** Resolved horizontal alignment ordinals, as carried in {@link ViewTile.align}. */
export const ALIGN_LEFT = 0;
export const ALIGN_CENTER = 1;
export const ALIGN_RIGHT = 2;

/** The numeric value did not fit and shows `#####` (Requirement 6.7). */
export const FLAG_OVERFLOW = 1 << 0;
/** The cell holds an error value, so its text is the error code (Requirement 16.4). */
export const FLAG_ERROR = 1 << 1;
/** The cell holds a formula. */
export const FLAG_FORMULA = 1 << 2;
/** The cell is blank. */
export const FLAG_EMPTY = 1 << 3;

/**
 * One rectangle of a sheet, resolved for painting.
 *
 * Indexing is row-major over the covered rectangle:
 * `(row - range.r0) * cols + (col - range.c0)`.
 */
export interface ViewTile {
  /** The normalized rectangle this tile covers. */
  range: Range;
  rows: number;
  cols: number;
  /** Display text per cell, `#####` and error codes included. */
  text: string[];
  /** Resolved alignment per cell. */
  align: Uint8Array;
  /** `FLAG_*` bits per cell. */
  flags: Uint8Array;
  /** Index into {@link ViewTile.styles} per cell. */
  styleRefs: Int32Array;
  /** Distinct effective styles in this tile, conditional overlay applied. */
  styles: RenderStyle[];
}

/** A sheet tab (Requirements 1.3, 1.11, 1.12). */
export interface SheetTab {
  id: SheetId;
  name: string;
  index: number;
}

/**
 * A sheet's geometry and tab state.
 *
 * Width and height overrides travel as sparse `[index, px]` pairs because that is
 * how the Data_Store holds them: a 5,000,000-row sheet with three resized rows
 * carries three pairs (Requirements 1.6, 1.7).
 */
export interface SheetView {
  sheet: SheetId;
  name: string;
  index: number;
  sheets: SheetTab[];
  colWidths: [number, number][];
  rowHeights: [number, number][];
  defaultColWidthPx: number;
  defaultRowHeightPx: number;
  /** Freeze origin, or `null` when nothing is frozen (Requirement 1.8). */
  freeze: Addr | null;
  /** Merged regions (Requirement 6.8). */
  merges: Range[];
  /** Bounding box of the non-empty cells (Requirement 3.5). */
  usedRange: Range;
  /**
   * Inclusive `[start, end]` runs of rows the active filters hide (Requirement 7.4).
   *
   * Runs rather than a row-per-entry list or a bitset: a filtered sheet hides a handful
   * of contiguous stretches, and the payload has to stay small enough to travel with
   * every geometry read. The rows are *hidden*, not deleted — the Data_Store still holds
   * them, and clearing the filters brings them back (Requirement 7.8).
   */
  hiddenRows: [number, number][];
}

/** What the formula bar and the cell editor show for one cell (Requirement 1.9). */
export interface CellContent {
  addr: Addr;
  /** Formula source for a formula cell, otherwise the formatted value. */
  input: string;
  /** What the grid paints, `#####` and error codes included. */
  display: string;
  value: CellValue;
  /** Formula source including the leading `=`, or `null` for a literal. */
  formula: string | null;
}

/** Style index used when a tile carries no style for a cell. */
export const DEFAULT_STYLE_INDEX = -1;

/**
 * Appearance of a cell nobody has formatted.
 *
 * Spelled out rather than imported from the Format_Manager's `DEFAULT_STYLE`,
 * which would be a runtime import into the main chunk. The two are asserted equal
 * by a unit test, so the duplication cannot rot silently.
 */
export const FALLBACK_STYLE: RenderStyle = Object.freeze({
  numFmtId: 0,
  bold: false,
  italic: false,
  underline: false,
  fontSizePx: 12,
  color: '#000000',
  background: 'transparent',
  hAlign: 'general',
  vAlign: 'bottom',
  wrap: false,
  borders: Object.freeze({
    top: Object.freeze({ line: 'none', color: '#000000' }),
    right: Object.freeze({ line: 'none', color: '#000000' }),
    bottom: Object.freeze({ line: 'none', color: '#000000' }),
    left: Object.freeze({ line: 'none', color: '#000000' }),
  }),
}) as RenderStyle;

/** The style behind a `styleRef`, falling back to {@link FALLBACK_STYLE}. */
export function styleOf(tile: ViewTile, ref: number): RenderStyle {
  return tile.styles[ref] ?? FALLBACK_STYLE;
}
